<template>
  <main class="py-10">
    <UContainer>
      <div class="mb-8">
        <UBreadcrumb :items="breadcrumbItems" class="mb-4" />
        <h1 class="text-3xl font-bold text-gray-900 dark:text-white">BI Hub</h1>
        <p class="text-gray-500 dark:text-gray-400 mt-1">
          Zentrale Übersicht Ihrer Analyse-Bereiche
        </p>
      </div>

      <div class="mb-12">
        <h2
          class="text-xl font-semibold mb-6 text-gray-800 dark:text-gray-200 flex items-center gap-2"
        >
          <UIcon name="i-heroicons-check-circle" class="text-green-500 w-6 h-6" />
          Verfügbare Analysen
        </h2>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <UCard
            v-for="section in implementedSections"
            :key="section.id"
            class="hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-green-500"
            @click="navigateTo(section.route)"
          >
            <div class="flex items-center gap-4">
              <div :class="`p-3 rounded-lg ${section.colorClass}`">
                <UIcon :name="section.icon" class="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 class="font-bold text-lg">{{ section.title }}</h3>
                <p class="text-sm text-gray-500 dark:text-gray-400">{{ section.description }}</p>
              </div>
            </div>
          </UCard>
        </div>
      </div>

      <div>
        <h2
          class="text-xl font-semibold mb-6 text-gray-800 dark:text-gray-200 flex items-center gap-2"
        >
          <UIcon name="i-heroicons-clock" class="text-blue-500 w-6 h-6" />
          Geplante Erweiterungen
        </h2>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <UCard
            v-for="section in upcomingSections"
            :key="section.id"
            class="hover:shadow-lg transition-shadow cursor-pointer opacity-80 grayscale-[0.5] hover:grayscale-0 hover:opacity-100"
            @click="navigateTo(section.route)"
          >
            <div class="flex items-center gap-4">
              <div :class="`p-3 rounded-lg ${section.colorClass}`">
                <UIcon :name="section.icon" class="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 class="font-bold text-lg">{{ section.title }}</h3>
                <p class="text-sm text-gray-500 dark:text-gray-400">{{ section.description }}</p>
              </div>
            </div>
          </UCard>
        </div>
      </div>
    </UContainer>
  </main>
</template>

<script lang="ts" setup>
import { useRouter } from 'vue-router'
import { computed } from 'vue'

const router = useRouter()

const props = defineProps<{
  customerNo: string
  tenantName: string
}>()

const breadcrumbItems = [
  { label: 'BI', to: `/customers/${props.customerNo}/tenants/${props.tenantName}/bi` },
]

const sections = [
  {
    id: 'customers',
    title: 'Kunden',
    description: 'Analyse der Kunden-Performance',
    icon: 'i-heroicons-users',
    colorClass: 'bg-blue-500',
    route: 'BiCustomerDashboard',
  },
  {
    id: 'suppliers',
    title: 'Lieferanten',
    description: 'Beschaffungs-Analyse',
    icon: 'i-heroicons-truck',
    colorClass: 'bg-orange-500',
    route: 'BiSupplierAnalysis',
  },
  {
    id: 'articles',
    title: 'Artikel',
    description: 'Produkt-Performance & Rankings',
    icon: 'i-heroicons-cube',
    colorClass: 'bg-green-500',
    route: 'BiArticleDashboard',
  },
  {
    id: 'sales',
    title: 'Verkaufsberichte',
    description: 'Vergleich ACT vs. PY',
    icon: 'i-heroicons-presentation-chart-line',
    colorClass: 'bg-purple-500',
    route: 'BiSalesReports',
  },
  {
    id: 'abc',
    title: 'ABC-Analyse',
    description: 'Klassifizierung von Kunden/Artikeln',
    icon: 'i-heroicons-adjustments-vertical',
    colorClass: 'bg-red-500',
    route: 'BiAbcAnalysis',
  },
  {
    id: 'documents',
    title: 'Belege',
    description: 'Monatliche Übersicht der Vorgänge',
    icon: 'i-heroicons-document-text',
    colorClass: 'bg-indigo-500',
    route: 'BiDocumentAnalysis',
  },
  {
    id: 'productGroups',
    title: 'Warengruppen',
    description: 'Umsatzverteilung nach Gruppen',
    icon: 'i-heroicons-tag',
    colorClass: 'bg-teal-500',
    route: 'BiProductGroupAnalysis',
  },
]

const implementedSections = computed(() =>
  sections.filter((s) => ['customers', 'suppliers', 'articles'].includes(s.id)),
)
const upcomingSections = computed(() =>
  sections.filter((s) => !['customers', 'suppliers', 'articles'].includes(s.id)),
)

const navigateTo = (routeName: string) => {
  router.push({
    name: routeName,
    params: {
      customerNo: props.customerNo,
      tenantName: props.tenantName,
    },
  })
}
</script>
